import {
  reportError,
  setObservabilityContext,
} from '@payments-checkout/shared/observability';
import { instance } from '@payments-checkout/shared/helpers/service/instance.service';
import { useQuery } from '@tanstack/react-query';
import type { AxiosError, AxiosResponse } from 'axios';
import { useEffect } from 'react';
import { SinglePaymentLinkResponse } from '../../types/paymentLink.types';

async function getPaymentLinkById(paymentLinkId: string) {
  const response = await instance.get<SinglePaymentLinkResponse>(
    `paymentlinks/${paymentLinkId}`
  );
  return response;
}

const usePaymentLinkId = (paymentLinkId: string) => {
  const query = useQuery<AxiosResponse<SinglePaymentLinkResponse>, AxiosError>({
    queryKey: ['getPaymentLink', paymentLinkId],
    queryFn: () => getPaymentLinkById(paymentLinkId),
    staleTime: Infinity,
    enabled: !!paymentLinkId,
    retry: false,
  });

  const accountSettings = query.data?.data?.account_settings;

  // `organization` não entra aqui: o analitica já publica o mesmo valor como
  // `project`, e publicar nos dois criaria atributo duplicado.
  useEffect(() => {
    setObservabilityContext({ merchant_id: accountSettings?.merchant_id });
  }, [accountSettings?.merchant_id]);

  // Link expirado, inválido ou 404 é o modo de falha mais comum do checkout, e
  // não tinha report nenhum: o interceptor de `instance` só cobre falha **sem**
  // resposta. `useEffect` porque `useQuery` do react-query 5 não tem `onError`.
  // `extractHttpContext` anexa `http_status`, então 404 se separa de 5xx no
  // Datadog sem meta extra.
  useEffect(() => {
    if (query.error) {
      reportError(query.error, { source: 'payment_link_fetch' });
    }
  }, [query.error]);

  return { ...query, data: query.data?.data };
};

export { getPaymentLinkById, usePaymentLinkId };
